export interface Product {
  id: number;
  name: string;
  variety?: string; // Optional variety field (e.g., "Iceberg" for "Lettuce")
  description: string;
  category: string; // Changed from enum to string for dynamic categories from database
  price?: string; // Optional - now stored per-fair in junction table
  availability?: 'in-season' | 'coming-soon' | 'sold-out'; // Optional - now stored per-fair in junction table
  image?: string;
  emoji?: string; // Optional emoji icon for the product
  is_organic?: boolean; // Optional - now stored per-fair in junction table (fair-specific organic certification)
  is_featured?: boolean; // Optional - now stored per-fair in junction table (fair-specific featured status)
  fair_count?: number; // Optional - number of fairs selling this product (Issue #278)
  // Issue #674: Fair-specific product catalog fields
  base_price?: string; // Base price before fair-specific adjustments
  production_time_days?: number; // Days needed to produce made-to-order items
  is_made_to_order?: boolean; // True if product is custom/made-to-order
  max_preorders_per_fair?: number; // Maximum pre-orders allowed per fair
}

export interface ProductCreate {
  name: string;
  variety?: string;
  description: string;
  category: string;
  price?: string;
  availability?: 'in-season' | 'coming-soon' | 'sold-out';
  image?: string;
  emoji?: string;
  is_organic?: boolean;
  is_featured?: boolean;
  base_price?: string;
  production_time_days?: number;
  is_made_to_order?: boolean;
  max_preorders_per_fair?: number;
}

export interface ProductUpdate {
  name?: string;
  variety?: string;
  description?: string;
  category?: string;
  price?: string;
  availability?: 'in-season' | 'coming-soon' | 'sold-out';
  image?: string;
  emoji?: string;
  is_organic?: boolean;
  is_featured?: boolean;
  base_price?: string;
  production_time_days?: number;
  is_made_to_order?: boolean;
  max_preorders_per_fair?: number;
}

export interface ProductListResponse {
  items: Product[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ProductWithFair extends Product {
  fair_id: number;
  fair_name: string; // Issue #891: Removed duplicate optional declaration
  fair_city?: string;
  fair_state?: string;
  fair_email: string;
}

// Issue #674: Fair Vendor Product Catalog types
export interface FairVendorProduct {
  id: number;
  fair_id: number;
  vendor_id: number;
  product_id: number;
  fair_specific_price?: string; // Fair-specific price (overrides base_price)
  quantity_available?: number; // Available quantity for this fair
  is_featured: boolean; // Highlight this product at this fair
  is_available_for_preorder: boolean; // Accept pre-orders for this product at this fair
  created_at: string;
  updated_at: string;
  product?: Product; // Full product details when included
  vendor?: {
    id: number;
    user_id: number;
    business_name: string;
    is_active: boolean;
  };
}

export interface FairVendorProductCreate {
  product_id: number;
  fair_specific_price?: string;
  quantity_available?: number;
  is_featured?: boolean;
  is_available_for_preorder?: boolean;
}

export interface FairVendorProductUpdate {
  fair_specific_price?: string;
  quantity_available?: number;
  is_featured?: boolean;
  is_available_for_preorder?: boolean;
}

export interface FairVendorProductListResponse {
  items: FairVendorProduct[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// ==============================================================================
// PRODUCT PRE-ORDER TYPES (Issue #675)
// ==============================================================================

// Re-export auto-generated types from Pydantic models (Issue #894)
export type { PreOrderStatus, PreOrderPaymentStatus } from './generated';

export interface ProductPreOrder {
  id: number;
  customer_id: number;
  product_id: number;
  fair_id: number;
  vendor_id: number;
  quantity: number;
  unit_price: string;
  total_price: string;
  special_instructions?: string;
  status: PreOrderStatus;
  payment_status: PreOrderPaymentStatus;
  stripe_payment_intent_id?: string;
  pickup_date?: string;
  confirmation_code: string;
  created_at: string;
  updated_at: string;
  // Related data
  product_name?: string;
  vendor_name?: string;
  fair_name?: string;
}

export interface ProductPreOrderCreate {
  product_id: number;
  fair_id: number;
  vendor_id: number;
  quantity: number;
  special_instructions?: string;
}

export interface ProductPreOrderUpdate {
  status?: PreOrderStatus;
  payment_status?: PreOrderPaymentStatus;
}

export interface ProductPreOrderListResponse {
  orders: ProductPreOrder[];
  total: number;
}

export interface MarkPickedUpRequest {
  confirmation_code: string;
}

// ==============================================================================
// PRODUCT FAIR PERFORMANCE TYPES (Issue #680, Issue #893)
// ==============================================================================

// Product performance at a specific fair
export interface ProductFairPerformanceCreate {
  product_id: number;
  vendor_id: number;
  fair_id: number;
  units_sold?: number;
  revenue?: string; // Decimal precision for currency
  customer_interest_level?: number; // 1-5 rating
  notes?: string; // HTML sanitized
}

export interface ProductFairPerformanceUpdate {
  units_sold?: number;
  revenue?: string; // Decimal precision for currency
  customer_interest_level?: number; // 1-5 rating
  notes?: string; // HTML sanitized
}

export interface ProductFairPerformanceResponse {
  id: number;
  product_id: number;
  vendor_id: number;
  fair_id: number;
  units_sold: number;
  revenue: string; // Decimal precision for currency
  customer_interest_level?: number; // 1-5 rating
  notes?: string;
  created_at: string;
  updated_at: string;
}

// Artist Fair Image types
export interface FairImage {
  id: number;
  fair_id: number;
  image_url: string;
  caption?: string;
  display_order: number;
  created_at?: string;
}

export interface FairImageCreate {
  image_url: string;
  caption?: string;
  display_order?: number;
}

export interface FairImageUpdate {
  image_url?: string;
  caption?: string;
  display_order?: number;
}

export interface Fair {
  id: number;
  name: string;
  description: string;
  products: Product[];
  categories: string[];
  payment_types?: PaymentTypeResponse[]; // Payment methods accepted
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
    coordinates: {
      lat: number;
      lng: number;
    }
  };
  image: string; // Legacy single image field (for backwards compatibility)
  images?: FairImage[]; // New multiple images field
  website?: string;
  social: {
    facebook?: string;
    instagram?: string;
  };
  email: string;
  phone?: string;
  hours?: string;
  // Multi-day scheduling fields (Issue #2, #11)
  start_date?: string; // Event start date (ISO format: YYYY-MM-DD)
  end_date?: string; // Event end date (ISO format: YYYY-MM-DD)
  total_slots?: number; // Total number of vendor booth spaces
  allows_per_day_reservation?: boolean; // Allow per-day slot reservations vs full-duration only
  is_test?: boolean; // Flag for test artist fairs
  is_featured?: boolean; // Flag for featured artist fair (sorted first in list)
  is_founding_market?: boolean; // Founding market badge for early-adopter markets (Issue #1318)
  has_attendant?: boolean | null; // True = attended, False = self-serve, null = unknown
  submitted_by?: number | null; // User ID who submitted this fair (Issue #69)
  has_managers?: boolean; // Whether fair has assigned managers (Issue #1452 - seeded fairs have has_managers=false)
  // Fair amenities/features (Issue #54)
  has_live_music?: boolean; // Live music performances
  has_alcohol?: boolean; // Beer/wine/alcohol available
  has_guest_appearances?: boolean; // Special guest appearances
  has_giveaways?: boolean; // Giveaways/raffles/prizes
  has_food_trucks?: boolean; // Food trucks on-site
  is_affiliated?: boolean; // Affiliated with organization/event
  // Dashboard statistics (Issue #82)
  next_date?: string; // Closest fair date to now (ISO format: YYYY-MM-DD)
  available_slots?: number; // Number of vendor booth spaces available
  vendor_count?: number; // Number of vendors with confirmed reservations
  // Extended operational details (Issue #149, #150)
  daily_hours?: string; // Operating hours each day (e.g., "9:00 AM - 5:00 PM")
  load_in_time?: string; // When vendors can arrive to set up
  load_out_time?: string; // When vendors must leave
  parking_info?: string; // Parking instructions for vendors
  is_indoor?: boolean; // Indoor vs outdoor venue
  weather_policy?: string; // Rain date, cancellation policy, refund terms
  // Marketing information (Issue #149, #150)
  attendee_entry_fee?: string; // Issue #890: Changed from number to string for Decimal precision - Admission price for public
  expected_attendance?: number; // Estimated number of attendees
  fair_categories?: string[]; // Type: craft fair, farmers market, art show, etc.
  // Vendor requirements (Issue #149, #150)
  insurance_required?: boolean; // Do vendors need liability insurance?
  application_deadline?: string; // Last day to apply for booth (ISO format: YYYY-MM-DD)
  booth_includes?: string; // What's provided: tables, chairs, tents, etc.
  power_availability?: string; // Fair-wide electricity policy
  // Policies (Issue #149, #150)
  prohibited_items?: string; // What cannot be sold
  sales_tax_info?: string; // Vendor sales tax responsibilities
  photography_policy?: string; // Can fair photograph vendors/products for marketing?
  // Fair submission payment fields (Issue #151, #217)
  submission_fee_paid?: string; // Issue #890: Changed from number to string for Decimal precision - Amount paid for fair submission ($49 or discounted)
  submission_payment_status?: 'pending' | 'paid' | 'waived'; // Payment status with type safety
  is_draft?: boolean; // Draft mode - not publicly visible until payment or fee waived
  published_at?: string; // When fair was published (ISO format timestamp)
  // Slot offer configuration (Issue #640)
  offer_duration_hours?: number; // How long vendors have to accept slot offers (1-168 hours, default 48)
  // Fair following (Issue #1313)
  follower_count?: number; // Number of users following this fair
  is_following?: boolean; // True if current user is following this fair (requires authentication)
}

// Fair statistics for fair managers
export interface FairStats {
  total_slots: number;
  available_slots: number;
  reserved_slots: number;
  total_revenue: string; // Issue #890: Changed from number to string for Decimal precision
  pending_revenue: string; // Issue #890: Changed from number to string for Decimal precision
  vendor_count: number;
}

// Fair Manager Contact Settings (Issue #1076 - Phase 3 of #966)
export interface ContactSettingsResponse {
  show_contact_info: boolean;
  public_email: string | null;
  public_phone: string | null;
}

export interface ContactSettingsUpdate {
  show_contact_info: boolean;
  public_email?: string;
  public_phone?: string;
}

export interface User {
  id: number;
  email: string;
  // Legacy flags removed in Phase 4 (Issue #865): is_admin, is_fair_manager, is_vendor
  // Use RBAC utilities in src/utils/rbac.ts instead (roles, permissions)
  email_verified?: boolean;
  created_at?: string;
  // Issue #203 / #220: Pre-Launch Promotion Tracking
  promoted_from_prelaunch?: boolean;
  promotion_date?: string;  // ISO 8601 timestamp
  prelaunch_signup_id?: number;
  is_early_access?: boolean;
  ad_discount_percent?: number;
  free_ad_credits?: number;
  // Stripe Connect fields (Issue #153, #218) - admin/self only
  stripe_connect_account_id?: string | null;
  commission_rate_override?: number | null;
  // Issue #860: RBAC Migration - roles and permissions
  roles?: string[];  // Array of role names (e.g., ["Admin", "Fair Manager"])
  permissions?: Record<string, boolean>;  // Permission flags
}

// ==============================================================================
// ROLE MANAGEMENT TYPES (Issue #969)
// ==============================================================================

export interface Role {
  id: number;
  name: string;
  description: string;
  permissions: Record<string, boolean> | null;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  user_count?: number;
}

export interface UserWithRoles {
  id: number;
  email: string;
  display_name: string | null;
  email_verified: boolean;
  created_at: string;
  roles: Array<{
    id: number;
    name: string;
    granted_at: string;
    expires_at: string | null;
    granted_by: number | null;
    granted_by_email: string | null;
  }>;
}

export interface Permission {
  key: string;
  description: string;
  category: string;
}

export interface RoleAnalytics {
  role_distribution: Array<{
    role_id: number;
    role_name: string;
    user_count: number;
  }>;
  total_users: number;
  users_with_roles: number;
  users_without_roles: number;
  recent_changes_7d: number;
}

export interface UsersWithRolesResponse {
  items: UserWithRoles[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface UserRoleAssignRequest {
  user_id: number;
  role_id: number;
}

export interface UserRoleResponse {
  message: string;
  user_id: number;
  role_id: number;
}

export interface SetUserRolesRequest {
  role_name: string;
  is_active: boolean;
}

export interface SetUserRolesResponse {
  message: string;
  user_id: number;
  roles: string[];
}

// Product Category types (database-driven)
export interface ProductCategoryResponse {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  icon?: string;
  display_order: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ProductCategoryCreate {
  name: string;
  display_name: string;
  description?: string;
  icon?: string;
  display_order: number;
  is_active: boolean;
}

export interface ProductCategoryUpdate {
  name?: string;
  display_name?: string;
  description?: string;
  icon?: string;
  display_order?: number;
  is_active?: boolean;
}

// Payment Type types (database-driven)
export interface PaymentTypeResponse {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  icon?: string;
  display_order: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface PaymentTypeCreate {
  name: string;
  display_name: string;
  description?: string;
  icon?: string;
  display_order: number;
  is_active: boolean;
}

export interface PaymentTypeUpdate {
  name?: string;
  display_name?: string;
  description?: string;
  icon?: string;
  display_order?: number;
  is_active?: boolean;
}

// User Profile, Favorites, and Reviews types
export interface UserProfile {
  id: number;
  email: string;
  display_name?: string;
  bio?: string;
  avatar_url?: string;
  email_notifications: boolean;
  review_notifications: boolean;
  // Legacy flags removed in Phase 4 (Issue #865): is_admin, is_fair_manager
  // Use RBAC utilities in src/utils/rbac.ts instead
  created_at?: string;
  // Issue #203 / #220: Pre-Launch Promotion Tracking
  promoted_from_prelaunch?: boolean;
  promotion_date?: string;  // ISO 8601 timestamp
  prelaunch_signup_id?: number;
  is_early_access?: boolean;
  ad_discount_percent?: number;
  free_ad_credits?: number;
  // Stripe Connect fields (Issue #153, #218) - admin/self only
  stripe_connect_account_id?: string | null;
  commission_rate_override?: number | null;
  // Issue #860: RBAC Migration - roles and permissions
  roles?: string[];  // Array of role names
  permissions?: Record<string, boolean>;  // Permission flags
}

/**
 * Type guard to check if user has an active early access discount
 * Issue #220: Pre-Launch Promotion Tracking
 * @param profile - User profile or null
 * @returns true if user has a valid discount percentage > 0
 */
export const hasEarlyAccessDiscount = (profile: UserProfile | null): boolean => {
  return !!(profile?.ad_discount_percent && profile.ad_discount_percent > 0);
};

/**
 * Type guard to check if user has free ad credits remaining
 * Issue #220: Pre-Launch Promotion Tracking
 * @param profile - User profile or null
 * @returns true if user has free ad credits > 0
 */
export const hasFreeAdCredits = (profile: UserProfile | null): boolean => {
  return !!(profile?.free_ad_credits && profile.free_ad_credits > 0);
};

/**
 * Type guard to check if user is an early access user
 * Issue #220: Pre-Launch Promotion Tracking
 * @param profile - User profile or null
 * @returns true if user has early access flag set
 */
export const isEarlyAccessUser = (profile: UserProfile | null): boolean => {
  return profile?.is_early_access === true;
};

export interface UserProfileUpdate {
  display_name?: string;
  bio?: string;
  avatar_url?: string;
  email_notifications?: boolean;
  review_notifications?: boolean;
}

export interface UserFavorite {
  id: number;
  user_id: number;
  fair_id: number;
  fair_name?: string;
  fair_city?: string;
  fair_state?: string;
  created_at?: string;
}

export interface UserFavoriteCreate {
  fair_id: number;
  fair_name?: string;
  fair_city?: string;
  fair_state?: string;
}

export interface Review {
  id: number;
  user_id: number;
  fair_id: number;
  fair_name?: string;
  fair_city?: string;
  fair_state?: string;
  rating: number; // 1-5
  title: string;
  comment: string;
  created_at?: string;
  updated_at?: string;
  user_display_name?: string;
  user_email?: string;
}

export interface ReviewCreate {
  fair_id: number;
  fair_name?: string;
  fair_city?: string;
  fair_state?: string;
  rating: number; // 1-5
  title: string;
  comment: string;
}

export interface ReviewUpdate {
  rating?: number;
  title?: string;
  comment?: string;
}

// Product Unit types removed (Issue #85)
// Product units system was unnecessary for vendor booth reservation system

// Paginated API Response type
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  pages?: number;
}

/**
 * Amenity information for slot proximity search (Issue #678, Issue #763 - L-3)
 *
 * Represents nearby amenities for booth/slot locations, used in advanced slot search
 * and booth comparison tools to help vendors find optimal booth placements.
 *
 * @example
 * ```typescript
 * const amenity: AmenityInfo = {
 *   type: 'restroom',
 *   distance: 50,  // 50 meters away
 *   name: 'Main Restrooms'
 * };
 * ```
 */
export interface AmenityInfo {
  /**
   * Type of amenity (e.g., 'restroom', 'food', 'entrance', 'parking')
   * Used for filtering slots by proximity to specific amenity types
   */
  type: string;

  /**
   * Distance from the slot to the amenity in meters
   * Used for proximity-based search and sorting
   */
  distance: number;

  /**
   * Optional human-readable name of the amenity
   * @example 'Main Entrance', 'Food Court #2'
   */
  name?: string;
}

// Slot types (Issue #12 - Slot Search & Filter)
export interface Slot {
  id: number;
  fair_id: number;
  slot_number: string;
  dimensions?: string; // e.g., "10x10 feet"
  has_power: boolean;
  slot_type: string; // 'common', 'preferred', 'premium', etc.
  base_price: string; // Issue #890: Changed from number to string for Decimal precision
  price_override?: string; // Issue #890: Changed from number to string for Decimal precision (Issue #152)
  is_available: boolean;
  // Advanced search fields (Issue #678)
  location_x?: number;
  location_y?: number;
  /**
   * Estimated foot traffic score (0-100 scale)
   * @default 50
   */
  estimated_foot_traffic?: number;
  /**
   * Whether this booth is a corner booth
   * Backend always returns this field (defaults to false)
   * @default false
   */
  is_corner_booth: boolean; // Issue #892: Required field (backend always returns it)
  distance_to_entrance?: number; // meters
  nearby_amenities?: AmenityInfo[]; // Parsed from JSON
  created_at?: string;
  updated_at?: string;
}

export interface SlotCreate {
  fair_id: number;
  slot_number: string;
  dimensions?: string;
  has_power?: boolean;
  slot_type?: string;
  base_price: string; // Issue #890: Changed from number to string for Decimal precision
  price_override?: string; // Issue #890: Changed from number to string for Decimal precision (Issue #152)
  is_available?: boolean;
}

export interface SlotUpdate {
  slot_number?: string;
  dimensions?: string;
  has_power?: boolean;
  slot_type?: string;
  base_price?: string; // Issue #890: Changed from number to string for Decimal precision
  price_override?: string; // Issue #890: Changed from number to string for Decimal precision (Issue #152)
  is_available?: boolean;
}

export interface SlotFilters {
  fair_id?: number;
  slot_type?: string[];
  has_power?: boolean;
  min_price?: number;
  max_price?: number;
  is_available?: boolean;
  search?: string; // Search by slot_number
}

export interface SlotListResponse {
  items: Slot[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface SlotAvailability {
  slot_id: number;
  slot_number: string;
  is_available: boolean;
  conflicting_reservations: Array<{
    id: number;
    vendor_id: number;
    status: string;
    reservation_type: string;
    selected_dates?: string[];
  }>;
  available_dates?: string[];
  unavailable_dates?: string[];
}

// Slot Availability Summary (Issue #348)
export interface SlotAvailabilitySummary {
  available_slots: number;
  total_slots: number;
  min_price: number;
  slot_types: string[];
}

// Fair Manager Slot Management with Reservation Details (Issue #400)
export interface ReservationDetailForSlot {
  reservation_id: number;
  vendor_id: number;
  vendor_name: string;
  vendor_email: string;
  vendor_phone?: string;
  status: string; // 'pending', 'confirmed', 'cancelled'
  payment_status: string; // 'pending', 'paid', 'refunded'
  reservation_type: string; // 'full_duration' or 'per_day'
  selected_dates?: string[]; // For per_day reservations
  price_paid: string; // Issue #890: Changed from number to string for Decimal precision
  created_at: string;
}

export interface SlotWithReservation extends Slot {
  reservation?: ReservationDetailForSlot; // null if slot is empty
  is_reserved: boolean; // Helper flag for UI filtering
}

export interface SlotWithReservationListResponse {
  items: SlotWithReservation[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Slot Pricing Template types (Issue #152)
export interface SlotPricingTemplate {
  id: number;
  fair_id: number;
  slot_type: string;
  base_price: string; // Issue #890: Changed from number to string for Decimal precision
  template_name?: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export interface SlotPricingTemplateCreate {
  fair_id: number;
  slot_type: string;
  base_price: string; // Issue #890: Changed from number to string for Decimal precision
  template_name?: string;
  description?: string;
}

export interface SlotPricingTemplateUpdate {
  base_price?: string; // Issue #890: Changed from number to string for Decimal precision
  template_name?: string;
  description?: string;
}

export interface SlotPricingTemplateListResponse {
  items: SlotPricingTemplate[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface SlotPriceOverrideRequest {
  price_override: string | null; // Issue #890: String for Decimal precision, null to clear override and use template price
}

// Bulk Slot Creation (Issue #980)
export interface BulkSlotCreate {
  fair_id: number;
  slot_prefix: string;
  start_number: number;
  end_number: number;
  dimensions?: string;
  has_power?: boolean;
  slot_type?: string;
  base_price: string; // Issue #890: Changed from number to string for Decimal precision
}

// Issue #893: Updated to match backend BulkSlotCreateResponse exactly
export interface BulkSlotCreateResponse {
  success: boolean;
  created_count: number;
  slots: Slot[];
  message: string;
}

// Slot Comparison (Issue #678, Issue #980)
export interface SlotCompareRequest {
  slot_ids: number[]; // 2-3 slot IDs to compare
}

export interface SlotComparisonResponse {
  slots: Slot[];
}

// Slot Availability Matrix (Issue #966 - Phase 2, Issue #980)
export interface SlotAvailabilityMatrix {
  fair_id: number;
  fair_name: string;
  start_date: string; // ISO format: YYYY-MM-DD
  end_date: string; // ISO format: YYYY-MM-DD
  slots: Array<{
    slot_id: number;
    slot_number: string;
    dates: Record<string, boolean>; // date -> availability (true = available, false = reserved/unavailable)
  }>;
}

// Slot Response (Issue #980)
// Type alias to prevent drift - backend returns same structure as Slot
export type SlotResponse = Slot;

// Slot Availability Response (Issue #980)
export interface SlotAvailabilityResponse {
  slot_id: number;
  slot_number: string | null; // Can be null when slot is missing (backend line 3798)
  is_available: boolean;
  conflicting_reservations: Array<{
    id: number;
    reservation_type: string;
    status: string; // Backend includes reservation status (line 3824)
    dates: 'all' | string[]; // Backend uses 'all' for full_duration or array of dates for per_day (lines 3828, 3841)
  }>;
  available_dates: string[] | null; // Backend returns null for full_duration, array for per_day (line 3880)
  unavailable_dates: string[] | null; // Backend returns null for full_duration, array for per_day (line 3881)
}

// Vendor Profile types (Issue #13)
export interface Vendor {
  id: number;
  user_id: number;
  business_name: string;
  description?: string;
  bio?: string;
  website?: string;
  social_media_facebook?: string;
  social_media_instagram?: string;
  product_types?: string[]; // JSON array of product categories
  portfolio_images?: string[]; // JSON array of Cloudinary URLs
  is_active: boolean;
  average_rating?: number; // Average review rating (Issue #1179)
  created_at?: string;
  updated_at?: string;
}

export interface VendorCreate {
  business_name: string;
  description?: string;
  bio?: string;
  website?: string;
  social_media_facebook?: string;
  social_media_instagram?: string;
  product_types?: string[];
}

export interface VendorUpdate {
  business_name?: string;
  description?: string;
  bio?: string;
  website?: string;
  social_media_facebook?: string;
  social_media_instagram?: string;
  product_types?: string[];
  is_active?: boolean;
}

// Public vendor profile (user_id excluded for privacy)
export interface VendorPublicProfile {
  id: number;
  business_name: string;
  description?: string;
  bio?: string;
  website?: string;
  social_media_facebook?: string;
  social_media_instagram?: string;
  product_types?: string[];
  portfolio_images?: string[];
  is_active: boolean;
  created_at?: string;
  rating_summary?: {
    average_rating: number | null;
    total_reviews: number;
  };
}

export interface VendorListResponse {
  items: VendorPublicProfile[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Public product showcase item for customer vendor profile (Issue #1200)
export interface VendorPublicProductItem {
  id: number;
  name: string;
  description?: string | null;
  category?: string | null;
  base_price?: string | null; // Issue #890: Decimal precision - always string
  image?: string | null;
  emoji?: string | null;
  is_made_to_order: boolean;
}

// Upcoming fair appearance for customer vendor profile (Issue #1200)
export interface VendorUpcomingFair {
  id: number;
  name: string;
  city?: string | null;
  state?: string | null;
  start_date: string;
  end_date: string;
  image?: string | null;
}

// Admin vendor list response includes user_id for audit purposes
export interface AdminVendorListResponse {
  items: Vendor[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Admin Reservation Management types (Issue #972)
export interface AdminReservationResponse {
  id: number;
  vendor_id: number;
  vendor_business_name: string;
  vendor_email: string;
  slot_id: number;
  slot_number: string;
  fair_id: number;
  fair_name: string;
  reservation_type: 'full_duration' | 'per_day';
  selected_dates?: string[];
  status: 'pending' | 'confirmed' | 'cancelled';
  price_paid: string;
  payment_status: 'pending' | 'paid' | 'refunded';
  stripe_payment_intent_id?: string;
  stripe_checkout_session_id?: string;
  platform_commission_rate?: number;
  platform_commission_amount?: number;
  fair_admin_payout_amount?: number;
  payment_method?: string;
  discount_code_id?: number;
  discount_code_applied?: string;
  discount_amount?: number;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AdminReservationListResponse {
  items: AdminReservationResponse[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface AdminReservationUpdate {
  reservation_type?: 'full_duration' | 'per_day';
  selected_dates?: string[];
  status?: 'pending' | 'confirmed' | 'cancelled';
  price_paid?: string;
  payment_status?: 'pending' | 'paid' | 'refunded';
  notes?: string;
}

export interface PortfolioImageUploadResponse {
  success: boolean;
  image_url: string;
  message: string;
}

export interface VendorReservation {
  id: number;
  fair_id: number;
  fair_name: string;
  fair_start_date?: string;
  fair_end_date?: string;
  fair_location?: string;
  slot_id: number;
  slot_number: string;
  reservation_type: 'full_duration' | 'per_day';
  selected_dates?: string[];
  status: 'pending' | 'confirmed' | 'cancelled';
  price_paid: string; // Issue #890: Changed from number to string for Decimal precision
  payment_status: 'pending' | 'paid' | 'refunded';
  notes?: string;
  created_at?: string;
}

// Vendor Booth Preferences types (Issue #677)
export interface VendorBoothPreferences {
  id: number;
  vendor_id: number;
  prefers_corner: boolean;
  prefers_high_traffic: boolean;
  prefers_near_entrance: boolean;
  requires_power: boolean;
  prefers_away_from_competitors: boolean;
  preferred_neighbors?: (number | string)[]; // vendor_ids or product types
  budget_min?: string; // Issue #890: Changed from number to string for Decimal precision
  budget_max?: string; // Issue #890: Changed from number to string for Decimal precision
  min_dimensions?: string; // e.g., "10x10"
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface VendorBoothPreferencesUpdate {
  prefers_corner?: boolean;
  prefers_high_traffic?: boolean;
  prefers_near_entrance?: boolean;
  requires_power?: boolean;
  prefers_away_from_competitors?: boolean;
  preferred_neighbors?: (number | string)[];
  budget_min?: string; // Issue #890: Changed from number to string for Decimal precision
  budget_max?: string; // Issue #890: Changed from number to string for Decimal precision
  min_dimensions?: string;
  notes?: string;
}

export interface BoothMatchResult {
  slot: {
    id: number;
    fair_id: number;
    slot_number: string;
    dimensions?: string;
    has_power: boolean;
    slot_type: string;
    base_price: string; // Issue #890: Changed from number to string for Decimal precision
    price_override?: string; // Issue #890: Changed from number to string for Decimal precision
    is_available: boolean;
  };
  match_score: number; // 0-100
  match_reasons: string[];
}

export interface BoothMatchRequest {
  vendor_id?: number;
  preferences?: {
    prefers_corner?: boolean;
    prefers_high_traffic?: boolean;
    prefers_near_entrance?: boolean;
    requires_power?: boolean;
    prefers_away_from_competitors?: boolean;
    preferred_neighbors?: (number | string)[];
    budget_min?: string; // Issue #890: Changed from number to string for Decimal precision
    budget_max?: string; // Issue #890: Changed from number to string for Decimal precision
    min_dimensions?: string;
    notes?: string;
  };
}

// Fair-Vendor-Product relationship types (Issue #80)
export interface FairVendorResponse {
  vendor_id: number;
  business_name: string;
  description?: string;
  website?: string;
  product_types: string[];
  portfolio_images: string[];
  reservation_id: number;
  slot_number: string;
  reservation_type: string;
  selected_dates?: string[];
}

export interface FairProductResponse {
  product_id: number;
  name: string;
  variety?: string;
  description?: string;
  category: string;
  image?: string;
  emoji?: string;
  vendor_id: number;
  vendor_name: string;
}

// Reservation System types (Issue #14)
export interface ReservationCreate {
  vendor_id: number;
  slot_id: number;
  reservation_type: 'full_duration' | 'per_day';
  selected_dates?: string[]; // Required for per_day
  notes?: string;
  discount_code?: string; // Issue #375 - Optional discount code
}

export interface ReservationUpdate {
  reservation_type?: 'full_duration' | 'per_day';
  selected_dates?: string[];
  notes?: string;
}

export interface ReservationStatusUpdate {
  status: 'pending' | 'confirmed' | 'cancelled';
  admin_notes?: string;
}

export interface Reservation {
  id: number;
  vendor_id: number;
  vendor_name?: string;
  vendor_email?: string;  // Issue #378 - Vendor contact email
  slot_id: number;
  slot_number: string;
  fair_id: number;
  fair_name: string;
  fair_start_date?: string;
  fair_end_date?: string;
  reservation_type: 'full_duration' | 'per_day';
  selected_dates?: string[];
  status: 'pending' | 'confirmed' | 'cancelled';
  price_paid: string; // Issue #890: Changed from number to string for Decimal precision
  payment_status: 'pending' | 'paid' | 'refunded';
  // Issue #153: Stripe Payment Integration fields (Issue #216)
  stripe_payment_intent_id?: string;
  stripe_checkout_session_id?: string;
  platform_commission_rate?: number;
  platform_commission_amount?: string; // Issue #890: Changed from number to string for Decimal precision
  fair_admin_payout_amount?: string; // Issue #890: Changed from number to string for Decimal precision
  payment_method?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ReservationDetail extends Reservation {
  slot_dimensions?: string;
  slot_has_power?: boolean;
  slot_type?: string;
  fair_address?: string;
  fair_city?: string;
  fair_state?: string;
}

// Reservation Analytics types (Issue #378)
export interface ReservationAnalytics {
  total_confirmed: number;
  total_revenue: string;  // Decimal from backend as string
  average_reservation_value: string;  // Decimal from backend as string
  occupancy_rate: number;  // Percentage (0-100)
}

export interface ReservationListResponse {
  items: Reservation[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  analytics?: ReservationAnalytics;  // Issue #378 - Vendor signup analytics
}

export interface SlotAvailabilityCheck {
  is_available: boolean;
  conflicting_reservations: Array<{
    id: number;
    reservation_type: string;
    selected_dates?: string[];
  }>;
  available_dates: string[];
  unavailable_dates: string[];
}

// Reservation Flow state for wizard
export interface ReservationFlowState {
  slot: Slot | null;
  fair: Fair | null;
  vendor_id: number | null;
  reservation_type: 'full_duration' | 'per_day' | null;
  selected_dates: string[];
  rental_items: ReservationRentalItemCreate[];
  special_requests: string;
  discount_code: string | null; // Issue #375 - Discount code entered by user
  discount_valid: boolean; // Issue #375 - Whether code has been validated
  discount_type: 'percentage' | 'fixed_amount' | 'free' | null; // Issue #375 - Type of discount
  discount_value: string | null; // Issue #890: Changed from number to string for Decimal precision - Discount value
  original_price: string; // Issue #890: Changed from number to string for Decimal precision - Price before discount
  total_price: string; // Issue #890: Changed from number to string for Decimal precision
  step: number;
}

// Waitlist System types (Issue #62)
export interface WaitlistEntry {
  id: number;
  fair_id: number;
  vendor_id: number;
  user_id: number;
  position: number;
  status: 'waiting' | 'notified' | 'converted' | 'expired' | 'cancelled';
  slot_preference?: string | null;
  notified_at?: string | null;
  offer_expires_at?: string | null;
  converted_at?: string | null;
  cancelled_at?: string | null;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface WaitlistDetailResponse extends WaitlistEntry {
  vendor_business_name?: string;
  vendor_email?: string;
  fair_name?: string;
  fair_start_date?: string;
  fair_end_date?: string;
}

export interface WaitlistPositionResponse {
  fair_id: number;
  vendor_id: number;
  position: number | null;
  status?: 'waiting' | 'notified' | 'converted' | 'expired' | 'cancelled' | null;
  estimated_wait_time?: string | null;
  total_in_queue: number;
  is_on_waitlist: boolean;
}

export interface WaitlistStatsResponse {
  fair_id: number;
  fair_name: string;
  total_waiting: number;
  total_notified: number;
  total_converted: number;
  total_expired: number;
  total_cancelled: number;
  conversion_rate: number;
  average_wait_time_days: number | null;
}

export interface WaitlistAnalyticsResponse {
  total_active_waitlists: number;
  total_fairs_with_waitlists: number;
  overall_conversion_rate: number;
  average_position_at_conversion: number | null;
  total_expired_offers: number;
}

export interface WaitlistListResponse {
  items: WaitlistEntry[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface WaitlistJoinRequest {
  vendor_id: number;
  fair_id: number;
  slot_preference?: string;
  notes?: string;
}

// ============================================================================
// Notification Types (Issue #496)
// ============================================================================

export type NotificationType =
  | 'waitlist_join'
  | 'position_change'
  | 'slot_offer'
  | 'offer_expiring'
  | 'offer_accepted'
  | 'offer_declined'
  | 'removed_from_waitlist';

export interface Notification {
  id: number;
  user_id: number;
  type: NotificationType;
  title: string;
  message: string;
  link?: string;
  read: boolean;
  created_at: string;
}

export interface NotificationListResponse {
  notifications: Notification[];
  unread_count: number;
  total_count: number;  // Issue #595: Total number of notifications
  has_more: boolean;    // Issue #595: Whether there are more notifications to load
}

// Notification Snooze Types (Issue #823, #1109)
export interface NotificationSnoozeRequest {
  duration_minutes?: number; // Duration in minutes (mutually exclusive with until_timestamp)
  until_timestamp?: string; // ISO 8601 timestamp when snooze should expire
}

export interface NotificationSnoozeResponse {
  snooze_until: string | null; // ISO 8601 timestamp when snooze expires
  snooze_active: boolean; // Whether snooze is currently active
}

// ============================================================================
// Notification Preferences Types (Issue #594, #822, #823, #1109)
// ============================================================================

export type NotificationFrequency = 'realtime' | 'daily' | 'weekly' | 'disable';

export interface NotificationPreferences {
  // Database metadata
  id: number;
  user_id: number;
  created_at: string;
  updated_at: string;
  // Per-type notification toggles
  waitlist_join_enabled: boolean;
  position_change_enabled: boolean;
  slot_offer_enabled: boolean;
  offer_expiring_enabled: boolean;
  offer_accepted_enabled: boolean;
  offer_declined_enabled: boolean;
  removed_from_waitlist_enabled: boolean;
  // Notification frequency
  frequency: NotificationFrequency;
  // Do Not Disturb (Issue #823)
  do_not_disturb: boolean;
  // Notification snooze (Issue #1109)
  snooze_until: string | null; // ISO 8601 timestamp when snooze expires
  // Quiet hours settings (Issue #822)
  quiet_hours_enabled: boolean;
  quiet_hours_start?: string | null;  // HH:MM format (e.g., "22:00"), null when unset
  quiet_hours_end?: string | null;    // HH:MM format (e.g., "08:00"), null when unset
  quiet_hours_timezone?: string;  // Timezone string (e.g., "America/New_York", "UTC")
}

export interface NotificationPreferencesUpdate {
  // Per-type notification toggles
  waitlist_join_enabled?: boolean;
  position_change_enabled?: boolean;
  slot_offer_enabled?: boolean;
  offer_expiring_enabled?: boolean;
  offer_accepted_enabled?: boolean;
  offer_declined_enabled?: boolean;
  removed_from_waitlist_enabled?: boolean;
  // Notification frequency
  frequency?: NotificationFrequency;
  // Do Not Disturb (Issue #823)
  do_not_disturb?: boolean;
  // Quiet hours settings (Issue #822)
  quiet_hours_enabled?: boolean;
  quiet_hours_start?: string | null;  // HH:MM format, null to clear
  quiet_hours_end?: string | null;    // HH:MM format, null to clear
  quiet_hours_timezone?: string;
}

// ============================================================================
// Rental Equipment Types (Issue #56)
// ============================================================================

export interface RentalItem {
  id: number;
  fair_id: number;
  name: string;
  description?: string;
  category?: string; // 'furniture', 'lighting', 'audio', 'display', 'power', 'tent', 'other'
  price_per_day: string; // Issue #890: Changed from number to string for Decimal precision
  price_per_event?: string; // Issue #890: Changed from number to string for Decimal precision
  quantity_available: number;
  quantity_reserved: number;
  image_url?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface RentalItemCreate {
  name: string;
  description?: string;
  category?: string;
  price_per_day: string; // Issue #890: Changed from number to string for Decimal precision
  price_per_event?: string; // Issue #890: Changed from number to string for Decimal precision
  quantity_available?: number;
  image_url?: string;
  is_active?: boolean;
}

export interface RentalItemUpdate {
  name?: string;
  description?: string;
  category?: string;
  price_per_day?: string; // Issue #890: Changed from number to string for Decimal precision
  price_per_event?: string; // Issue #890: Changed from number to string for Decimal precision
  quantity_available?: number;
  image_url?: string;
  is_active?: boolean;
}

export interface RentalItemListResponse {
  items: RentalItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ReservationRentalItem {
  id: number;
  reservation_id: number;
  rental_item_id: number;
  rental_item_name: string;
  rental_item_category?: string;
  quantity: number;
  price_paid: string; // Issue #890: Changed from number to string for Decimal precision
  created_at: string;
}

export interface ReservationRentalItemCreate {
  rental_item_id: number;
  quantity?: number;
}

export interface RentalStats {
  fair_id: number;
  fair_name: string;
  total_rental_items: number;
  total_quantity_available: number;
  total_quantity_reserved: number;
  total_reservations_with_rentals: number;
  total_rental_revenue: string; // Issue #890: Changed from number to string for Decimal precision
  popular_items: Array<{
    item_name: string;
    times_reserved: number;
    revenue: string; // Issue #890: Changed from number to string for Decimal precision
  }>;
  revenue_by_category: Array<{
    category: string;
    revenue: string; // Issue #890: Changed from number to string for Decimal precision
  }>;
}

// Discount Code types (Issue #375)
export interface DiscountCodeCreate {
  code: string;
  discount_type: 'percentage' | 'fixed_amount' | 'free';
  discount_value: string; // Issue #890: Changed from number to string for Decimal precision
  max_uses?: number | null;
  start_date: string; // ISO date format: YYYY-MM-DD
  end_date: string; // ISO date format: YYYY-MM-DD
  slot_type_filter?: string | null;
}

export interface DiscountCodeUpdate {
  discount_type?: 'percentage' | 'fixed_amount' | 'free';
  discount_value?: string; // Issue #890: Changed from number to string for Decimal precision
  max_uses?: number | null;
  start_date?: string; // ISO date format: YYYY-MM-DD
  end_date?: string; // ISO date format: YYYY-MM-DD
  slot_type_filter?: string | null;
  is_active?: boolean;
}

export interface DiscountCode {
  id: number;
  code: string;
  fair_id: number;
  discount_type: 'percentage' | 'fixed_amount' | 'free';
  discount_value: string; // Issue #890: Changed from number to string for Decimal precision
  max_uses: number | null;
  current_uses: number;
  start_date: string; // ISO date format: YYYY-MM-DD
  end_date: string; // ISO date format: YYYY-MM-DD
  slot_type_filter: string | null;
  is_active: boolean;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface DiscountCodeListResponse {
  items: DiscountCode[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface DiscountCodeValidateRequest {
  code: string;
  slot_id: number;
}

export interface DiscountCodeValidateResponse {
  is_valid: boolean;
  message: string;
  discount_type?: 'percentage' | 'fixed_amount' | 'free' | null;
  discount_value?: string | null; // Issue #890: Changed from number to string for Decimal precision
}

// Discount Code Analytics types (Issue #385, #399, #460)
export interface DiscountCodeAnalytics {
  code: string;
  total_redemptions: number;
  max_uses: number | null;
  usage_rate: number; // Percentage (0-100)
  total_discount_amount: string; // Decimal as string
  avg_discount_per_use: string; // Decimal as string
  revenue_impact: string; // Decimal as string (negative value)
  conversion_rate: number; // Percentage (0-100)
  redemptions_by_day?: Array<{ date: string; count: number }>; // Optional for summary_only mode
  slot_type_distribution?: Record<string, number>; // Optional for summary_only mode
}

// Advertisement Package types (Issue #86)
// Supported currency codes (matches backend SUPPORTED_CURRENCIES from Issue #99)
export type SupportedCurrency = 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD' | 'JPY' | 'CHF' | 'SEK' | 'NOK' | 'DKK' | 'NZD';

export interface AdPackage {
  id: number;
  name: string;
  description?: string;
  duration_days: number;
  price_cents: number;
  currency: SupportedCurrency;  // ISO 4217 currency code (Issue #99)
  priority_boost: number;
  max_ads_per_purchase: number;
  season?: 'spring' | 'summer' | 'fall' | 'winter' | 'year_round';
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface AdPackageCreate {
  name: string;
  description?: string;
  duration_days: number;
  price_cents: number;
  currency?: SupportedCurrency;  // ISO 4217 currency code (defaults to USD) (Issue #99)
  priority_boost?: number;
  max_ads_per_purchase?: number;
  season?: 'spring' | 'summer' | 'fall' | 'winter' | 'year_round';
  is_active?: boolean;
  display_order?: number;
}

export interface AdPackageUpdate {
  name?: string;
  description?: string;
  duration_days?: number;
  price_cents?: number;
  currency?: SupportedCurrency;  // ISO 4217 currency code (Issue #99)
  priority_boost?: number;
  max_ads_per_purchase?: number;
  season?: 'spring' | 'summer' | 'fall' | 'winter' | 'year_round';
  is_active?: boolean;
  display_order?: number;
}

export interface AdPackageListResponse {
  items: AdPackage[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Advertisement Purchase types (Issue #86)
export interface AdPurchaseCreate {
  package_id: number;
  vendor_id?: number;
  title: string;
  description?: string;
  ad_type: 'youtube' | 'etsy' | 'direct' | 'custom';
  target_url: string;
  image_url?: string;
  display_text?: string;
}

export interface AdPurchase {
  id: number;
  user_id: number;
  package_id: number;
  advertisement_id?: number;
  vendor_id?: number;
  purchase_date: string;
  amount_cents: number;
  currency: SupportedCurrency;  // Locked-in currency at purchase time (Issue #99)
  payment_status: 'pending' | 'completed' | 'failed' | 'refunded';
  payment_method?: string;
  transaction_id?: string;
  start_date?: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
  // Nested details
  package_name?: string;
  advertisement_title?: string;
  vendor_name?: string;
}

export interface AdPurchaseStatusUpdate {
  payment_status: 'pending' | 'completed' | 'failed' | 'refunded';
  payment_method?: string;
  transaction_id?: string;
}

export interface AdPurchaseListResponse {
  items: AdPurchase[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Advertisement Analytics types (Issue #975)
export interface AdPackageAnalytics {
  total_packages: number;
  active_packages: number;
  total_purchases: number;
  completed_purchases: number;
  pending_purchases: number;
  failed_purchases: number;
  refunded_purchases: number;
  total_revenue_cents: number;
  conversion_rate: number;
  packages_by_popularity: Array<{
    package_id: number;
    package_name: string;
    season: string;
    purchase_count: number;
    revenue_cents: number;
  }>;
  revenue_by_season: Record<string, number>;
  recent_purchases: AdPurchase[];
}

// ==================== Feature Flag Types (Issue #167) ====================

export interface FeatureFlagCreate {
  flag_key: string;
  flag_name: string;
  description?: string;
  is_enabled: boolean;
}

export interface FeatureFlagUpdate {
  is_enabled: boolean;
  flag_name?: string;
  description?: string;
}

export interface FeatureFlag {
  id: number;
  flag_key: string;
  flag_name: string;
  description?: string;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
  created_by_user_id?: number;
  updated_by_user_id?: number;
}

export interface FeatureFlagStatusResponse {
  flag_key: string;
  is_enabled: boolean;
  flag_name?: string;
}

export interface FeatureFlagListResponse {
  items: FeatureFlag[];
  total: number;
}

// Fair Manager Request types (Issue #148 Frontend, Issue #177)
export interface FairManagerRequestCreate {
  business_name: string;
  business_description?: string;
  website?: string;
  phone?: string;
  request_reason: string;
}

export interface FairManagerRequestResponse {
  id: number;
  user_id: number;
  user_email: string;
  business_name: string;
  business_description?: string;
  website?: string;
  phone?: string;
  request_reason: string;
  status: 'pending' | 'approved' | 'rejected';
  admin_notes?: string;
  requested_at: string;
  reviewed_at?: string;
  reviewed_by_email?: string;
}

export interface FairManagerRequestListResponse {
  items: FairManagerRequestResponse[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Fair Manager Payout Dashboard types (Issue #154)
export interface FairManagerDashboardStats {
  total_pending_reservations: number;
  total_confirmed_reservations: number;
  total_paid_reservations: number;
  total_cancelled_reservations: number;
  total_gross_revenue: string; // Issue #890: Changed from number to string for Decimal precision
  platform_commission: string; // Issue #890: Changed from number to string for Decimal precision
  net_payout: string; // Issue #890: Changed from number to string for Decimal precision
  commission_rate: number; // Percentage, not currency
  pending_approvals: number;
}

export interface PayoutItem {
  reservation_id: number;
  vendor_name: string;
  vendor_email: string;
  fair_name: string;
  slot_number: string;
  reservation_date: string | null;
  payment_date: string | null;
  gross_amount: string; // Issue #890: Changed from number to string for Decimal precision
  commission_amount: string; // Issue #890: Changed from number to string for Decimal precision
  net_amount: string; // Issue #890: Changed from number to string for Decimal precision
  payment_status: string;
  reservation_status: string;
}

export interface FairManagerPayoutsResponse {
  items: PayoutItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  total_gross: string; // Issue #890: Changed from number to string for Decimal precision
  total_commission: string; // Issue #890: Changed from number to string for Decimal precision
  total_net: string; // Issue #890: Changed from number to string for Decimal precision
}

// Calendar View types (Issue #211)
export interface FairCalendarEvent {
  fair_id: number;
  name: string;
  start_date: string;  // YYYY-MM-DD
  end_date: string;    // YYYY-MM-DD
  available_slots: number;
  total_slots: number;
  city?: string;
  state?: string;
  is_sold_out: boolean;
  has_waitlist: boolean;
}

export interface CalendarDayEvents {
  date: string;  // YYYY-MM-DD
  events: FairCalendarEvent[];
}

export interface CalendarMonthResponse {
  year: number;
  month: number;  // 1-12
  days: CalendarDayEvents[];
  total_events: number;
}

// Advertisement types (Issue #350)
export type AdType = 'youtube' | 'etsy' | 'direct' | 'custom';

export interface Advertisement {
  id: number;
  title: string;
  description: string | null;
  ad_type: AdType;
  target_url: string;
  image_url: string | null;
  thumbnail_url: string | null;
  display_text: string | null;
  is_active: boolean;
  priority: number;
  impressions: number;
  clicks: number;
  start_date: string | null;  // ISO string
  end_date: string | null;    // ISO string
  created_at: string | null;
  updated_at: string | null;
  created_by: number | null;
}

export interface AdvertisementListResponse {
  items: Advertisement[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Advertisement Create/Update types (Issue #985)
export interface AdvertisementCreate {
  title: string;
  description?: string | null;
  ad_type: AdType;
  target_url: string;
  image_url?: string | null;
  thumbnail_url?: string | null;
  display_text?: string | null;
  is_active?: boolean;
  priority?: number;
  start_date?: string | null;  // ISO string
  end_date?: string | null;    // ISO string
}

// Vendor-restricted advertisement update (Issue #1175)
export interface VendorAdvertisementUpdate {
  title?: string;
  description?: string | null;
  target_url?: string;
  image_url?: string | null;
  thumbnail_url?: string | null;
  display_text?: string | null;
}

// Admin advertisement update (all fields)
export interface AdvertisementUpdate {
  title?: string;
  description?: string | null;
  ad_type?: AdType;
  target_url?: string;
  image_url?: string | null;
  thumbnail_url?: string | null;
  display_text?: string | null;
  is_active?: boolean;
  priority?: number;
  start_date?: string;
  end_date?: string;
}

// Advertisement Analytics types (Issue #1175)
export interface AdvertisementAnalytics {
  id: number;
  title: string;
  ad_type: string;
  impressions: number;
  clicks: number;
  ctr: number;  // Click-through rate
  is_active: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Fair Inquiry System Types (Issue #377)
// ============================================================================

// Inquiry type enumeration
export type InquiryType = 'general' | 'reservation' | 'payment' | 'technical' | 'complaint' | 'other';

// Inquiry status enumeration
export type InquiryStatus = 'new' | 'in_progress' | 'waiting_for_vendor' | 'resolved' | 'closed';

// Inquiry priority enumeration
export type InquiryPriority = 'low' | 'normal' | 'high' | 'urgent';

// Create new inquiry request
export interface FairInquiryCreate {
  vendor_id: number;
  fair_id: number;
  inquiry_type: InquiryType;
  subject: string;
  message: string;
  priority?: InquiryPriority;
  reservation_id?: number;
}

// Basic inquiry response
export interface FairInquiryResponse {
  id: number;
  vendor_id: number;
  vendor_name: string;
  vendor_email: string;
  fair_id: number;
  fair_name: string;
  inquiry_type: InquiryType;
  subject: string;
  status: InquiryStatus;
  priority: InquiryPriority;
  reservation_id?: number;
  unread_count: number;  // Number of unread messages
  created_at: string;
  updated_at: string;
  last_message_at?: string;
}

// Detailed inquiry response with messages
export interface FairInquiryDetailResponse extends FairInquiryResponse {
  messages: InquiryMessageResponse[];
}

// Inquiry list response (paginated)
export interface FairInquiryListResponse {
  items: FairInquiryResponse[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Create new inquiry message
export interface InquiryMessageCreate {
  inquiry_id: number;
  message: string;
  is_from_vendor: boolean;
}

// Inquiry message response
export interface InquiryMessageResponse {
  id: number;
  inquiry_id: number;
  message: string;
  is_from_vendor: boolean;
  is_read: boolean;
  sender_name: string;
  sender_email: string;
  created_at: string;
}

// Update inquiry status/priority
export interface InquiryStatusUpdate {
  status?: InquiryStatus;
  priority?: InquiryPriority;
}

// =============================================================================
// Email Campaign Types (Issue #376)
// =============================================================================

// Target type for email campaign recipient selection
export type EmailCampaignTargetType =
  | 'all_vendors'
  | 'product_types'
  | 'geographic'
  | 'custom_list';

// Target filters for campaign targeting
export interface EmailCampaignTargetFilters {
  product_types?: string[];
  radius_miles?: number;
  custom_emails?: string[];
}

// Create email campaign request
export interface EmailCampaignCreate {
  subject: string;
  message: string;
  discount_code_id?: number;
  target_type: EmailCampaignTargetType;
  target_filters?: EmailCampaignTargetFilters;
}

// Email campaign response
export interface EmailCampaignResponse {
  id: number;
  fair_id: number;
  subject: string;
  message: string;
  discount_code?: string;
  target_type: EmailCampaignTargetType;
  total_recipients: number;
  sent_at?: string;
  created_at: string;
}

// Paginated list of email campaigns
export interface EmailCampaignListResponse {
  items: EmailCampaignResponse[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Timeline data point for analytics
export interface EmailCampaignTimelineData {
  date: string;
  opens: number;
  clicks: number;
}

// Top clicked link data for analytics
export interface EmailCampaignTopLink {
  link: string;
  clicks: number;
}

// Conversion data for analytics
export interface EmailCampaignConversion {
  vendor_name: string;
  reservation_id: number;
  booking_date: string;
}

// Email campaign analytics and performance metrics
export interface EmailCampaignAnalytics {
  campaign_id: number;
  subject: string;
  sent_at: string;
  total_sent: number;
  total_opened: number;
  total_clicked: number;
  total_converted: number;
  open_rate: number; // Percentage (0-100)
  click_rate: number; // Percentage (0-100)
  conversion_rate: number; // Percentage (0-100)
  discount_code_uses: number;
  timeline: EmailCampaignTimelineData[];
  top_clicked_links: EmailCampaignTopLink[];
  conversions: EmailCampaignConversion[];
}

// Event type for email campaign tracking
export type EmailCampaignEventType =
  | 'sent'
  | 'opened'
  | 'clicked'
  | 'converted';

// Create email campaign event (tracking)
export interface EmailCampaignEventCreate {
  campaign_id: number;
  vendor_id?: number;
  vendor_email: string;
  event_type: EmailCampaignEventType;
  event_data?: Record<string, unknown>;
}

// ============================================================================
// Fair Cancellation with Bulk Refunds Types (Issue #379)
// ============================================================================

// Individual refund result for a single reservation
export interface RefundDetailItem {
  reservation_id: number;
  vendor_email: string;
  amount: number;
  success: boolean;
  stripe_refund_id?: string;
  error?: string;
}

// Request to cancel a fair with automatic refunds
export interface FairCancellationRequest {
  cancellation_reason: string; // Max 1000 characters, HTML sanitized
}

// Response from fair cancellation operation with refund results
export interface FairCancellationResponse {
  fair_id: number;
  fair_name: string;
  cancelled_at: string; // ISO timestamp
  cancelled_by_user_id: number;
  cancellation_reason: string;
  total_reservations: number;
  refunds_processed: number;
  refunds_failed: number;
  emails_sent: number;
  emails_failed: number;
  refund_details: RefundDetailItem[];
}

// ============================================================================
// Phase 1: Fair-Specific Product Catalogs (Issue #674) - Agent #3 Prep
// ============================================================================

// NOTE: FairVendorProduct types are defined at lines 31-72 to avoid duplicates
// Use string type for fair_specific_price per CLAUDE.md currency rules

// Product with fair-specific information
export interface ProductWithFairInfo extends Product {
  fair_id: number;
  fair_name?: string;
  fair_specific_price?: string; // Issue #890: Changed from number to string for Decimal precision
  quantity_available?: number;
  is_featured_at_fair: boolean;
  is_available_for_preorder: boolean;
}

// Extended Product model fields (Agent #1 will add to db_models.py)
export interface ProductExtended extends Product {
  base_price?: string; // Issue #890: Changed from number to string for Decimal precision - Base price before fair-specific pricing
  production_time_days?: number; // Time to produce for made-to-order items
  is_made_to_order: boolean; // Custom/made-to-order product
  max_preorders_per_fair?: number; // Limit pre-orders per fair
}

// ============================================================================
// ============================================================================
// Phase 2: Interactive Booth Map (Issue #676) - Agent #3
// ============================================================================

// Booth shape types (Issue #676, Issue #893; expanded Issue #1639)
// Backend allow-list (api/models.py:3369): 'rectangle' | 'square' | 'corner' | 'L-shaped' | 'circle' | 'polygon'
export type BoothShape = 'rectangle' | 'square' | 'corner' | 'L-shaped' | 'circle' | 'polygon';

// Booth layout position and dimensions
export interface BoothLayout {
  id: number;
  fair_id: number;
  slot_id: number;
  x_coord: number; // X position on canvas (pixels or meters)
  y_coord: number; // Y position on canvas (pixels or meters)
  width: number; // Booth width
  height: number; // Booth height
  shape: BoothShape;
  rotation: number; // Rotation in degrees (0-360)
  created_at?: string;
  updated_at?: string;
}

// Amenity types (Issue #676, Issue #893; expanded Issue #1639)
// Backend allow-list (api/models.py:3423)
export type AmenityType =
  | 'restroom'
  | 'entrance'
  | 'food_court'
  | 'parking'
  | 'stage'
  | 'seating'
  | 'water'
  | 'waste'
  | 'exit'
  | 'electrical'
  | 'food'
  | 'info'
  | 'first_aid'
  | 'wifi'
  | 'other';

// Booth amenity (facilities on map)
export interface BoothAmenity {
  id: number;
  fair_id: number;
  name: string; // Display name (e.g., "North Restroom")
  type: AmenityType;
  x_coord: number;
  y_coord: number;
  icon?: string; // Emoji or icon identifier
  created_at?: string;
  updated_at?: string;
}

// Create booth layout (Issue #676, Issue #893)
export interface BoothLayoutCreate {
  fair_id: number; // Issue #893: Added fair_id to match backend BoothLayoutCreate
  slot_id: number;
  x_coord: number;
  y_coord: number;
  width: number;
  height: number;
  shape?: BoothShape;
  rotation?: number;
}

// Update booth layout (Issue #676, Issue #893)
export interface BoothLayoutUpdate {
  x_coord?: number;
  y_coord?: number;
  width?: number;
  height?: number;
  shape?: BoothShape;
  rotation?: number;
}

// Booth layout response (Issue #676, Issue #893)
export interface BoothLayoutResponse {
  id: number;
  fair_id: number;
  slot_id: number;
  x_coord: number;
  y_coord: number;
  width: number;
  height: number;
  shape: BoothShape;
  rotation: number;
  created_at?: string;
  updated_at?: string;
}

// Bulk upload booth layouts (Issue #966 - Phase 2, Issue #893)
// Note: layouts array omits fair_id since it's specified once at top level (matches backend BoothLayoutBase)
export interface BoothLayoutBulkUpload {
  fair_id: number;
  layouts: Omit<BoothLayoutCreate, 'fair_id'>[];
}

// Bulk upload response (Issue #966 - Phase 2)
export interface BoothLayoutBulkUploadResponse {
  message: string;
  created: number;    // Count of new layouts
  updated: number;    // Count of updated layouts
  total: number;      // Total layouts processed
}

// Create amenity (Issue #676, Issue #893)
export interface BoothAmenityCreate {
  fair_id: number; // Issue #893: Added fair_id to match backend BoothAmenityCreate
  name: string;
  type: AmenityType;
  x_coord: number;
  y_coord: number;
  icon?: string;
}

// Update amenity (Issue #676, Issue #893)
export interface BoothAmenityUpdate {
  name?: string;
  type?: AmenityType;
  x_coord?: number;
  y_coord?: number;
  icon?: string;
}

// Booth amenity response (Issue #676, Issue #893)
export interface BoothAmenityResponse {
  id: number;
  fair_id: number;
  name: string;
  type: AmenityType;
  x_coord: number;
  y_coord: number;
  icon?: string;
  created_at?: string;
  updated_at?: string;
}

// API slot with booth map specific fields (Issue #1078)
// This represents what the backend API actually returns
export interface BoothMapSlotApi extends Slot {
  is_high_traffic?: boolean;    // API field name
  is_near_entrance?: boolean;   // API field name
  // is_corner_booth already in Slot interface
}

// Slot format expected by InteractiveBoothMap component (Issue #1078, #1159)
// This represents what the frontend map component expects
export interface BoothMapSlot extends Slot {
  corner_booth: boolean;         // Frontend field name
  high_traffic_area: boolean;    // Frontend field name
  near_entrance: boolean;        // Frontend field name
  // Vendor information for reserved booths (Issue #1159: Customer booth details modal)
  vendor_id?: number;            // ID of vendor if booth is reserved
  vendor_name?: string;          // Vendor display name if booth is reserved
  vendor_business_name?: string; // Vendor business name if booth is reserved
  vendor_product_types?: string[]; // Product categories vendor sells
}

// Composite booth map data (for rendering)
// Issue #1078: Uses BoothMapSlotApi to represent actual API response
export interface BoothMapData {
  fair_id: number;
  fair_name: string;
  layouts: BoothLayout[];
  amenities: BoothAmenity[];
  slots: BoothMapSlotApi[]; // API returns slots with is_* field names
}

// Booth with layout and reservation info (for map display)
export interface BoothWithLayout extends BoothLayout {
  slot_number: string;
  slot_type: string;
  has_power: boolean;
  base_price: string; // Issue #890: Changed from number to string for Decimal precision
  price_override?: string; // Issue #890: Changed from number to string for Decimal precision
  is_available: boolean;
  // Reservation info if booked
  reservation?: {
    vendor_id: number;
    vendor_name: string;
    status: string;
    payment_status: string;
  };
}

// Interactive map view state
export interface BoothMapViewState {
  zoom: number; // Zoom level (1.0 = 100%)
  pan_x: number; // Pan offset X
  pan_y: number; // Pan offset Y
  selected_booth_id?: number; // Currently selected booth
  hover_booth_id?: number; // Currently hovered booth
  show_amenities: boolean; // Toggle amenity visibility
  show_available_only: boolean; // Filter to only available booths
  highlight_slot_type?: string; // Highlight specific slot type
}

// Distance calculation result
export interface BoothDistance {
  from_slot_id: number;
  to_amenity_id: number;
  amenity_name: string;
  amenity_type: AmenityType;
  distance_meters: number;
  distance_feet: number;
}

// Neighbor booth info
export interface BoothNeighbor {
  slot_id: number;
  slot_number: string;
  vendor_id?: number; // ID of vendor if booth is reserved (Issue #952)
  vendor_name?: string; // If booth is reserved
  distance?: number; // Distance in map units (Issue #952)
  direction: 'north' | 'south' | 'east' | 'west' | 'northeast' | 'northwest' | 'southeast' | 'southwest';
  is_available: boolean;
}

// ========================================
// Issue #679: Expense & Revenue Tracking for ROI Calculation
// ========================================

// Expense types enum
export type ExpenseType =
  | 'booth_fee'
  | 'travel'
  | 'materials'
  | 'marketing'
  | 'equipment_rental'
  | 'permits'
  | 'insurance'
  | 'labor'
  | 'other';

// Revenue source enum
export type RevenueSource =
  | 'product_sales'
  | 'preorders'
  | 'tips'
  | 'commissions'
  | 'other';

// Vendor fair expense
export interface VendorFairExpense {
  id: number;
  vendor_id: number;
  fair_id: number;
  expense_type: ExpenseType;
  amount: string; // Issue #890: Changed from number to string for Decimal precision
  currency: string;
  date: string; // ISO date string
  notes?: string;
  receipt_url?: string;
  created_at: string;
  updated_at: string;
}

// Create expense request
export interface VendorFairExpenseCreate {
  vendor_id: number;
  fair_id: number;
  expense_type: ExpenseType;
  amount: string; // Issue #890: Changed from number to string for Decimal precision
  currency?: string;
  date: string; // ISO date string
  notes?: string;
  receipt_url?: string;
}

// Update expense request
export interface VendorFairExpenseUpdate {
  expense_type?: ExpenseType;
  amount?: string; // Issue #890: Changed from number to string for Decimal precision
  currency?: string;
  date?: string; // ISO date string
  notes?: string;
  receipt_url?: string;
}

// Vendor fair revenue
export interface VendorFairRevenue {
  id: number;
  vendor_id: number;
  fair_id: number;
  revenue_source: RevenueSource;
  amount: string; // Issue #890: Changed from number to string for Decimal precision
  currency: string;
  date: string; // ISO date string
  notes?: string;
  created_at: string;
  updated_at: string;
}

// Create revenue request
export interface VendorFairRevenueCreate {
  vendor_id: number;
  fair_id: number;
  revenue_source: RevenueSource;
  amount: string; // Issue #890: Changed from number to string for Decimal precision
  currency?: string;
  date: string; // ISO date string
  notes?: string;
}

// Update revenue request
export interface VendorFairRevenueUpdate {
  revenue_source?: RevenueSource;
  amount?: string; // Issue #890: Changed from number to string for Decimal precision
  currency?: string;
  date?: string; // ISO date string
  notes?: string;
}

// Fair ROI calculation result
export interface FairROICalculation {
  vendor_id: number;
  fair_id: number;
  total_expenses: string; // Issue #890: Changed from number to string for Decimal precision
  total_revenue: string; // Issue #890: Changed from number to string for Decimal precision
  net_profit: string; // Issue #890: Changed from number to string for Decimal precision
  roi_percentage: number; // Percentage, not currency
  expense_breakdown: Record<string, string>; // Issue #890: Changed values from number to string - { "booth_fee": "100.00", "travel": "50.00", ... }
  revenue_breakdown: Record<string, string>; // Issue #890: Changed values from number to string - { "product_sales": "500.00", "tips": "20.00", ... }
}

// Vendor lifetime ROI
export interface VendorLifetimeROI {
  vendor_id: number;
  total_fairs: number;
  total_expenses: string; // Issue #890: Changed from number to string for Decimal precision
  total_revenue: string; // Issue #890: Changed from number to string for Decimal precision
  net_profit: string; // Issue #890: Changed from number to string for Decimal precision
  roi_percentage: number; // Percentage, not currency
  average_roi_per_fair: number; // Percentage, not currency
  best_performing_fair?: {
    fair_id: number;
    fair_name: string;
    roi: number; // Percentage, not currency
  };
  worst_performing_fair?: {
    fair_id: number;
    fair_name: string;
    roi: number; // Percentage, not currency
  };
}

// ============================================================================
// Pagination Types (Issue #768)
// ============================================================================

export interface PaginationMeta {
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PaginatedExpenseResponse {
  items: VendorFairExpense[];
  pagination: PaginationMeta;
}

export interface PaginatedRevenueResponse {
  items: VendorFairRevenue[];
  pagination: PaginationMeta;
}

// ============================================================================
// Vendor Fair Metrics Types (Issue #682)
// Post-Fair Survey & Satisfaction Tracking
// ============================================================================

export type WeatherImpact = 'positive' | 'neutral' | 'negative';

// Create vendor fair metrics (POST)
export interface VendorFairMetricsCreate {
  vendor_id: number;
  fair_id: number;
  reservation_id?: number;
  // Quantitative metrics
  foot_traffic_estimate?: number;
  customer_interactions?: number;
  business_cards_collected?: number;
  email_signups?: number;
  social_media_follows?: number;
  // Ratings (1-5)
  satisfaction_rating?: number;
  booth_location_rating?: number;
  fair_organization_rating?: number;
  // Feedback
  would_return?: boolean;
  feedback_text?: string;
  weather_impact?: WeatherImpact;
}

// Update vendor fair metrics (PUT)
export interface VendorFairMetricsUpdate {
  foot_traffic_estimate?: number;
  customer_interactions?: number;
  business_cards_collected?: number;
  email_signups?: number;
  social_media_follows?: number;
  satisfaction_rating?: number;
  booth_location_rating?: number;
  fair_organization_rating?: number;
  would_return?: boolean;
  feedback_text?: string;
  weather_impact?: WeatherImpact;
}

// Vendor fair metrics response (GET)
export interface VendorFairMetricsResponse {
  id: number;
  vendor_id: number;
  fair_id: number;
  reservation_id?: number;
  // Quantitative metrics
  foot_traffic_estimate?: number;
  customer_interactions?: number;
  business_cards_collected?: number;
  email_signups?: number;
  social_media_follows?: number;
  // Ratings
  satisfaction_rating?: number;
  booth_location_rating?: number;
  fair_organization_rating?: number;
  // Feedback
  would_return?: boolean;
  feedback_text?: string;
  weather_impact?: WeatherImpact;
  // Timestamps
  submitted_at: string;
  created_at: string;
  updated_at: string;
}

// Vendor satisfaction trend over time
export interface VendorSatisfactionTrend {
  fair_id: number;
  fair_name: string;
  fair_date: string; // ISO date
  satisfaction_rating?: number;
  booth_location_rating?: number;
  fair_organization_rating?: number;
  would_return?: boolean;
  foot_traffic_estimate?: number;
  customer_interactions?: number;
}

// Fair manager feedback summary (aggregate/anonymized)
export interface FairManagerFeedbackSummary {
  fair_id: number;
  fair_name: string;
  total_responses: number;
  // Average ratings
  average_satisfaction_rating?: number;
  average_booth_location_rating?: number;
  average_fair_organization_rating?: number;
  // Would return statistics
  would_return_yes: number;
  would_return_no: number;
  would_return_percentage?: number;
  // Aggregate metrics
  average_foot_traffic?: number;
  average_customer_interactions?: number;
  // Weather impact distribution
  weather_positive_count: number;
  weather_neutral_count: number;
  weather_negative_count: number;
  // Anonymized feedback
  feedback_comments: string[];
}

// Expense type display names
export const EXPENSE_TYPE_LABELS: Record<ExpenseType, string> = {
  booth_fee: 'Booth Fee',
  travel: 'Travel',
  materials: 'Materials',
  marketing: 'Marketing',
  equipment_rental: 'Equipment Rental',
  permits: 'Permits',
  insurance: 'Insurance',
  labor: 'Labor',
  other: 'Other'
};

// Revenue source display names
export const REVENUE_SOURCE_LABELS: Record<RevenueSource, string> = {
  product_sales: 'Product Sales',
  preorders: 'Pre-orders',
  tips: 'Tips',
  commissions: 'Commissions',
  other: 'Other'
};

// Issue #680: Business Intelligence Analytics Types

export interface ProductRanking {
  product_id: number;
  product_name: string;
  total_units_sold: number;
  total_revenue: string; // Decimal as string
}

export interface FairComparisonData {
  fair_id: number;
  fair_name: string;
  total_revenue: string; // Decimal as string
  total_expenses: string; // Decimal as string
}

export interface SeasonalInsight {
  month: number; // 1-12
  month_name: string;
  year: number;
  total_revenue: string; // Decimal as string
}

// Issue #681: Vendor Profile Analytics & Discovery Metrics

export type VendorProfileViewSource = 'search' | 'fair_page' | 'direct_link' | 'featured' | 'recommendation';
export type DiscoverySource = 'search' | 'fair_page' | 'profile' | 'recommendation' | 'waitlist';

export interface TrackProfileViewRequest {
  vendor_id: number;
  viewer_user_id?: number;
  session_id: string;
  source: VendorProfileViewSource;
  dnt_header?: string;
}

export interface VendorProfileViewResponse {
  id: number;
  vendor_id: number;
  viewer_user_id?: number;
  session_id: string;
  source: string;
  viewed_at: string; // ISO datetime
}

export interface TrackSearchImpressionRequest {
  vendor_id: number;
  search_query?: string;
  search_filters?: Record<string, unknown>;
  position_in_results: number;
  clicked: boolean;
  session_id: string;
}

export interface BulkSearchImpressionRequest {
  impressions: TrackSearchImpressionRequest[];
  dnt_header?: string;
}

export interface VendorSearchImpressionResponse {
  id: number;
  vendor_id: number;
  search_query?: string;
  search_filters?: string; // JSON string
  position_in_results: number;
  clicked: boolean;
  session_id: string;
  created_at: string; // ISO datetime
}

export interface ProfileViewsAnalyticsRequest {
  start_date?: string; // ISO datetime
  end_date?: string; // ISO datetime
  granularity?: 'day' | 'week' | 'month';
}

export interface ProfileViewsAnalyticsResponse {
  total_views: number;
  unique_visitors: number;
  views_by_source: Record<string, number>;
  views_over_time: Array<{
    date: string;
    views: number;
  }>;
}

export interface SearchPerformanceResponse {
  total_impressions: number;
  total_clicks: number;
  click_through_rate: number; // Percentage
  average_position: number;
  top_queries: Array<{
    query: string;
    impressions: number;
    clicks: number;
  }>;
  top_filters: Array<{
    filter: string;
    impressions: number;
  }>;
}

export interface ConversionFunnelResponse {
  profile_views: number;
  search_impressions: number;
  search_clicks: number;
  reservations: number;
  profile_to_reservation_rate: number; // Percentage
  search_to_reservation_rate: number; // Percentage
}

export interface DiscoverySourcesResponse {
  sources: Record<string, number>;
  total_reservations: number;
  most_effective_source?: string;
}

// ============================================================================
// Vendor Review & Rating Types (Issue #683)
// ============================================================================

export interface VendorReviewCreate {
  vendor_id: number;
  fair_id: number;
  rating: number; // 1-5
  review_text?: string; // Max 1000 chars
}

export interface VendorReviewUpdate {
  rating?: number; // 1-5
  review_text?: string; // Max 1000 chars
}

export interface VendorReview {
  id: number;
  vendor_id: number;
  customer_id: number;
  fair_id: number;
  rating: number;
  review_text?: string;
  verified_purchase: boolean;
  helpful_count: number;
  is_flagged: boolean;
  is_published: boolean;
  created_at: string; // ISO datetime
  updated_at: string; // ISO datetime
  // Populated fields
  customer_display_name?: string;
  fair_name?: string;
  admin_notes?: string; // Admin moderation notes (Issue #973)
  user_marked_helpful?: boolean;
}

export interface VendorReviewListResponse {
  reviews: VendorReview[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface VendorRatingSummary {
  vendor_id: number;
  average_rating?: number; // 0.00 to 5.00
  total_reviews: number;
  rating_distribution: Record<number, number>; // {1: count, 2: count, ...}
  verified_reviews_count: number;
}

export interface VendorReviewEligibility {
  eligible: boolean;
  reason: string;
  verified_purchase: boolean;
  existing_review_id?: number;
}

export interface VendorReviewAdminUpdate {
  is_flagged?: boolean;
  is_published?: boolean;
  admin_notes?: string;
}

// ============================================================================
// Vendor Badge Types (Issue #684, #954)
// ============================================================================

export type BadgeType = 'achievement' | 'verification' | 'milestone' | 'special';

export interface VendorBadge {
  id: number;
  name: string;
  description: string;
  icon?: string; // Emoji or URL
  badge_type: BadgeType;
  requirements?: string; // JSON string
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface VendorBadgeAward {
  id: number;
  vendor_id: number;
  badge_id: number;
  awarded_at: string;
  expires_at?: string;
  awarded_by_admin_id?: number; // NULL = auto-awarded
  notes?: string;
  badge?: VendorBadge; // Nested badge details
}

export interface VendorBadgeProgress {
  badge: VendorBadge;
  earned: boolean;
  progress: Record<string, string | number | boolean>; // e.g., {'rating': '4.2/4.5', 'reviews': '8/10'}
}

export interface VendorBadgeCreate {
  name: string;
  description: string;
  icon?: string;
  badge_type: BadgeType;
  requirements?: string;
  is_active?: boolean;
  display_order?: number;
}

export interface VendorBadgeUpdate {
  name?: string;
  description?: string;
  icon?: string;
  badge_type?: BadgeType;
  requirements?: string;
  is_active?: boolean;
  display_order?: number;
}

export interface VendorBadgeAwardCreate {
  vendor_id: number;
  badge_id: number;
  expires_at?: string;
  notes?: string;
}

export interface VendorBadgeListResponse {
  items: VendorBadge[];
  total: number;
}

// ============================================================================
// Admin Analytics Interfaces (Issue #630)
// ============================================================================

export interface DateRangeFilter {
  start_date: string;
  end_date: string;
  fair_id?: number;
}

export interface WaitlistPerformanceMetrics {
  total_joins: number;
  status_breakdown: Record<string, number>;
  average_position_when_offered?: number;
  average_time_on_waitlist_days?: number;
}

export interface ConversionFunnelMetrics {
  total_joins: number;
  notified_count: number;
  converted_count: number;
  notification_rate: number; // percentage
  acceptance_rate: number; // percentage
  overall_conversion_rate: number; // percentage
}

export interface PeakJoinTime {
  day_of_week: number; // 0 = Sunday, 6 = Saturday
  hour: number; // 0-23
  count: number;
}

export interface PopularFair {
  fair_id: number;
  fair_name: string;
  joins_count: number;
}

export interface UserBehaviorMetrics {
  peak_join_times: PeakJoinTime[];
  most_popular_fairs: PopularFair[];
  average_decision_time_hours?: number;
}

export interface RealTimeStats {
  active_waitlist_entries: number;
  pending_slot_offers: number;
  recent_accepts_24h: number;
  recent_declines_24h: number;
}

export interface WaitlistAnalyticsResponse {
  date_range: DateRangeFilter;
  waitlist_performance: WaitlistPerformanceMetrics;
  conversion_funnel: ConversionFunnelMetrics;
  user_behavior: UserBehaviorMetrics;
  real_time_stats: RealTimeStats;
}

// ============================================================================
// API Error Handling Types (Issue #964)
// ============================================================================

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: Record<string, unknown>;
}

// ============================================================================
// Fair Manager Missing Endpoints Types (Issue #966 - Phase 1)
// ============================================================================

// Fair Submission Payment Flow
export interface FairSubmissionCheckoutResponse {
  checkout_url: string;
  fair_id: number;
  amount_cents: number;
}

export interface FairSubmissionStatusResponse {
  fair_id: number;
  submission_fee_paid: boolean;
  submission_payment_status: 'pending' | 'paid' | 'waived' | 'failed';
  is_draft: boolean;
  published_at: string | null;
}

// Fair Manager Onboarding types (Issue #1455)
export interface OnboardingProgress {
  id: number;
  application_id: number;
  user_id: number;
  fair_id: number;
  current_step: string;
  completed: boolean;
  completed_at: string | null;
  tos_accepted: boolean;
  tos_accepted_at: string | null;
  profile_completed: boolean;
  contact_info_completed: boolean;
  payment_setup_completed: boolean;
  fair_review_completed: boolean;
  progress_data: Record<string, unknown>;
  token_expires_at: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface OnboardingProgressSummary {
  percentage: number;
  steps_completed: number;
  total_steps: number;
  current_step: string;
  next_step: string | null;
}

export interface OnboardingStatusResponse {
  onboarding: OnboardingProgress;
  progress: OnboardingProgressSummary;
  fair: Fair;
  application: Record<string, unknown>; // FairManagerApplication type
}

export interface OnboardingStepData {
  step: string;
  data?: Record<string, unknown>;
}

export interface OnboardingProfileData {
  display_name?: string;
  bio?: string;
  avatar_url?: string;
}

export interface OnboardingContactData {
  public_email?: string;
  public_phone?: string;
  show_contact_info?: boolean;
}

export interface OnboardingPaymentData {
  stripe_connect_account_id?: string;
}

export interface OnboardingFairReviewData {
  fair_description?: string;
  fair_name?: string;
  fair_website?: string;
}

// Slot Price Override type is defined earlier in the file (line ~741) - duplicate removed